'use client';

import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@startuptools/shad-ui/card';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoIcon from '@mui/icons-material/Info';
import { VestingFrequency } from '@startuptools/common/stock-options';
import { VestingKind } from '@startuptools/common/stock-options';
import { Trans, useLingui } from '@lingui/react';
import * as Sentry from '@sentry/react';
import { DateFormat } from '@startuptools/ui/DateFormat';
import * as React from 'react';
import {
  GqlVestingOccasionStatus,
  GqlWarrantParticipant,
  GqlWarrantParticipantVestingOccasion,
} from '../graphql/base-types.graphql';

import { DateTime } from 'luxon';
import { useTheme } from '@startuptools/ui/Theme';
import BigNumber from 'bignumber.js';
import { formatNumber, NumberFormat } from '@startuptools/ui/NumberFormat';
import { Button } from '@startuptools/ui/Button';
import { RichUserText } from '@startuptools/ui/RichUserText';

export const DisplayVesting = ({ kind }: { kind: VestingKind }) => {
  switch (kind) {
    case VestingKind.Employment:
      return <Trans id="Employment" />;
    case VestingKind.BoardMembership:
      return <Trans id="Board Membership" />;
    case VestingKind.AdvisoryBoard:
      return <Trans id="Advisory Board Membership" />;
    case VestingKind.None:
      return <Trans id="No vesting" />;
    case VestingKind.Custom:
      return <Trans id="Other vesting" />;
    default:
      Sentry.captureException(new Error(`Unknown vesting kind: ${kind}`));
      return null;
  }
};

export const DisplayVestingFrequency = ({ frequency }: { frequency: VestingFrequency }) => {
  switch (frequency) {
    case VestingFrequency.Monthly:
      return <Trans id="Monthly" />;
    case VestingFrequency.Quarterly:
      return <Trans id="Quarterly" />;
    case VestingFrequency.Biyearly:
      return <Trans id="Biyearly" />;
    case VestingFrequency.Yearly:
      return <Trans id="Yearly" />;
    default:
      Sentry.captureException(new Error(`Unknown vesting frequency: ${frequency}`));
      return null;
  }
};

interface ExplanationProps {
  explanationPrimary?: string | null;
  explanationSecondary?: string | null;
  fetchExplanation: () => Promise<unknown>;
  loading: boolean;
}

function VestingExplanation({ loading, explanationPrimary, explanationSecondary, fetchExplanation }: ExplanationProps) {
  const { i18n } = useLingui();

  const explanation = i18n.locale === 'en' ? explanationSecondary : explanationPrimary;

  if (!explanation) {
    return (
      <div className="flex flex-col items-start gap-2 p-4 bg-muted/20 rounded-lg">
        <p className="text-sm text-muted-foreground">
          <Trans id="Want to know more about how it works?" />
        </p>
        <Button variant="outlined" size="sm" onClick={fetchExplanation} loading={loading}>
          <Trans id="Explain" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">
        <Trans id="Explanation" />
      </p>
      <p className="text-sm">
        <RichUserText>{explanation}</RichUserText>
      </p>
    </div>
  );
}

export function VestingChart({
  vestingOccasions,
  wpp,
  explanationProps,
}: {
  vestingOccasions: Pick<GqlWarrantParticipantVestingOccasion, 'vestingDate' | 'totalVested' | 'status'>[];
  wpp: Pick<
    GqlWarrantParticipant,
    | 'quantity'
    | 'quantityLeft'
    | 'vestedQuantity'
    | 'vestingKind'
    | 'vestingCliffDuration'
    | 'vestingDuration'
    | 'vestingFrequency'
    | 'vestingEndDate'
    | 'wontVestQuantity'
    | 'vestedQuantityOnEndDate'
    | 'vestedQuantity'
    | 'vestingExplanationPrimaryLang'
    | 'vestingExplanationSecondaryLang'
  >;
  explanationProps?: Omit<ExplanationProps, 'vestingExplanationPrimaryLang' | 'vestingExplanationSecondaryLang'>;
}) {
  const { i18n } = useLingui();
  const theme = useTheme();
  const colors = {
    [GqlVestingOccasionStatus.Vested]: theme.palette.success[300],
    [GqlVestingOccasionStatus.ToVest]: theme.palette.primary[300],
    [GqlVestingOccasionStatus.WontVest]: theme.palette.neutral[300],
  };
  const { vestingKind, vestingCliffDuration, vestingDuration, vestingFrequency, vestingEndDate } = wpp;
  const chartData = React.useMemo(() => {
    return vestingOccasions
      .filter(v => Boolean(v.vestingDate))
      .map(occasion => {
        const displayDate = DateTime.fromISO(occasion.vestingDate!).toFormat('yyyy-MM-dd');

        return {
          date: occasion.vestingDate!,
          displayDate,
          shares: occasion.totalVested,
          status: occasion.status,
        };
      });
  }, [vestingOccasions]);

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>
          <Trans id="Vesting Schedule" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {vestingKind === VestingKind.Custom ? (
          <div className="flex h-full w-full items-center justify-center">
            <p className="text-muted-foreground">
              <Trans id="This option holding has a custom vesting schedule." />
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 p-4 bg-muted/20 rounded-lg">
              <div className="flex items-start space-x-2">
                <LooksOneIcon />
                <div>
                  <p className="text-sm font-medium leading-none mb-1">
                    <Trans id="Quantity" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <NumberFormat locale={i18n.locale} maximumFractionDigits={0} roundingMode="trunc">
                      {wpp.quantity}
                    </NumberFormat>
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <CalendarMonthIcon />
                <div>
                  <p className="text-sm font-medium leading-none mb-1">
                    <Trans id="Start Date" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <DateFormat locale={i18n.locale}>{vestingOccasions[0]?.vestingDate}</DateFormat>
                  </p>
                </div>
              </div>

              {vestingEndDate && (
                <div className="flex items-start space-x-2">
                  <CalendarMonthIcon />
                  <div>
                    <p className="text-sm font-medium leading-none mb-1">
                      <Trans id="Termination date" />
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <DateFormat locale={i18n.locale}>{vestingEndDate}</DateFormat>
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start space-x-2">
                <CalendarMonthIcon />
                <div>
                  <p className="text-sm font-medium leading-none mb-1">
                    <Trans id="End Date" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <DateFormat locale={i18n.locale}>{vestingOccasions.at(-1)?.vestingDate}</DateFormat>
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <InfoIcon />
                <div>
                  <p className="text-sm font-medium leading-none mb-1">
                    <Trans id="Vesting connected to" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <DisplayVesting kind={vestingKind} />
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <InfoIcon />
                <div>
                  <p className="text-sm font-medium leading-none mb-1">
                    <Trans id="Vesting duration" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <Trans id="{number} months" values={{ number: vestingDuration }} />
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <InfoIcon />
                <div>
                  <p className="text-sm font-medium leading-none mb-1">
                    <Trans id="Vesting Frequency" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <DisplayVestingFrequency frequency={vestingFrequency} />
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-2">
                <InfoIcon />
                <div>
                  <p className="text-sm font-medium leading-none mb-1">
                    <Trans id="Cliff" />
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <Trans id="{number} months" values={{ number: vestingCliffDuration }} />
                  </p>
                </div>
              </div>
            </div>

            {explanationProps && (
              <VestingExplanation
                explanationPrimary={wpp.vestingExplanationPrimaryLang}
                explanationSecondary={wpp.vestingExplanationSecondaryLang}
                {...explanationProps}
              />
            )}

            <div className="flex flex-wrap gap-4 mb-2">
              <div className="flex items-center">
                <div
                  className="h-4 w-4 rounded mr-2"
                  style={{ backgroundColor: colors[GqlVestingOccasionStatus.Vested] }}
                ></div>
                <span className="text-sm">
                  <Trans id="Vested" /> (
                  <NumberFormat locale={i18n.locale} maximumFractionDigits={0} roundingMode="trunc">
                    {wpp.vestedQuantity}
                  </NumberFormat>
                  )
                </span>
              </div>
              <div className="flex items-center">
                <div
                  className="h-4 w-4 rounded mr-2"
                  style={{ backgroundColor: colors[GqlVestingOccasionStatus.ToVest] }}
                ></div>
                <span className="text-sm">
                  <Trans id="To Be Vested" /> (
                  <NumberFormat locale={i18n.locale} maximumFractionDigits={0} roundingMode="ceil">
                    {new BigNumber(wpp.vestedQuantityOnEndDate ?? '0').minus(wpp.vestedQuantity ?? '0')}
                  </NumberFormat>
                  )
                </span>
              </div>
              {vestingEndDate && (
                <div className="flex items-center">
                  <div
                    className="h-4 w-4 rounded mr-2"
                    style={{ backgroundColor: colors[GqlVestingOccasionStatus.WontVest] }}
                  ></div>
                  <span className="text-sm">
                    <Trans id="Won't Vest" /> (
                    <NumberFormat locale={i18n.locale} maximumFractionDigits={0} roundingMode="ceil">
                      {wpp.wontVestQuantity}
                    </NumberFormat>
                    )
                  </span>
                </div>
              )}
            </div>

            <div className="h-[400px] w-full border rounded-lg p-4">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 70 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="displayDate"
                      angle={-45}
                      textAnchor="end"
                      height={70}
                      tickMargin={20}
                      interval="preserveStartEnd"
                      minTickGap={50}
                    />
                    <YAxis />
                    <Tooltip
                      formatter={value => [
                        `${formatNumber(value as string, { locale: i18n.locale, roundingMode: 'trunc', maximumFractionDigits: 0 })}`,
                        i18n.t('Vested'),
                      ]}
                      labelFormatter={label => i18n.t('Date: {date}', { date: label })}
                    />

                    <Bar
                      dataKey="shares"
                      name={i18n.t('Vested')}
                      radius={[4, 4, 0, 0]}
                      fill={colors[GqlVestingOccasionStatus.Vested]} // Default color
                      isAnimationActive={false} // Disable animation to avoid rendering issues
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors[entry.status]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <p className="text-muted-foreground">
                    <Trans id="No vesting data available." />.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
